import * as React from 'react';
import {
  useReactTable,
  createColumnHelper,
  getCoreRowModel,
  flexRender
} from '@tanstack/react-table';

import classes from './VisitorsReportsTable.module.scss';
import { FC } from 'react';
import Skeleton from '../../../Skeleton/Skeleton';

type EmployersReportTableType = {
  date: string;
  registred: string;
  calledFromQueue: string;
  served: string;
  redirected: string;
  missed: string;
  isSummary?: boolean;
};

const columnSizes = {
  dateSize: 220,
  registredSize: 200,
  calledFromQueueSize: 200,
  servedSize: 200,
  redirectedSize: 200,
  missedSize: 200,
};

const {
  dateSize,
  registredSize,
  calledFromQueueSize,
  servedSize,
  redirectedSize,
  missedSize,
} = columnSizes;

type VisitorsReportsTableProps = {
  visitorsReportDate: EmployersReportTableType[];
  isLoadingData: boolean;
};

const VisitorsReportTable: FC<VisitorsReportsTableProps> = ({
  visitorsReportDate,
  isLoadingData
}) => {
  const columnHelper = createColumnHelper<EmployersReportTableType>();

  const summaryStyle = (isSummary?: boolean) => (isSummary ? { fontWeight: 700 } : undefined);

  const categoriesColumns = [
    columnHelper.accessor('date', {
      header: () => <div className={classes.headerContent}>Дата</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={summaryStyle(row.original.isSummary)}>
          {getValue()}
        </div>
      ),
      size: dateSize
    }),
    columnHelper.accessor('registred', {
      header: () => <div className={classes.headerContent}>Зарегистрировано</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={summaryStyle(row.original.isSummary)}>
          {getValue()}
        </div>
      ),
      size: registredSize
    }),
    columnHelper.accessor('calledFromQueue', {
      header: () => <div className={classes.headerContent}>Вызвано из очереди</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={summaryStyle(row.original.isSummary)}>
          {getValue()}
        </div>
      ),
      size: calledFromQueueSize
    }),
    columnHelper.accessor('served', {
      header: () => <div className={classes.headerContent}>Обслужено</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={summaryStyle(row.original.isSummary)}>
          {getValue()}
        </div>
      ),
      size: servedSize
    }),
    columnHelper.accessor('redirected', {
      header: () => <div className={classes.headerContent}>Перенаправлено</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={summaryStyle(row.original.isSummary)}>
          {getValue()}
        </div>
      ),
      size: redirectedSize
    }),
    columnHelper.accessor('missed', {
      header: () => <div className={classes.headerContent}>Не пришли</div>,
      cell: ({ row, getValue }) => (
        <div className={classes.colunmContent} style={summaryStyle(row.original.isSummary)}>
          {getValue()}
        </div>
      ),
      size: missedSize
    })
  ];

  const columns = categoriesColumns;
  const [activeRow, setActiveRow] = React.useState(null);

  const handleClick = (id: string) => {
    setActiveRow(id);
  };

  const tableData = React.useMemo(
    () => (isLoadingData ? Array(5).fill({}) : visitorsReportDate || []),
    [isLoadingData, visitorsReportDate]
  );
  const tableColumns = React.useMemo(
    () =>
      isLoadingData
        ? columns.map((column) => ({
            ...column,
            cell: () => <Skeleton />
          }))
        : columns,
    [isLoadingData, columns]
  );

  const table = useReactTable({
    data: tableData,
    columns: tableColumns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    getCoreRowModel: getCoreRowModel()
  });
  return (
    <div className={classes.tableContainer}>
      <table className={classes.table}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{ width: `${header.getSize()}px` }}>
                    <div className={classes.thTitle}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                      <div
                        {...{
                          onDoubleClick: () => header.column.resetSize(),
                          onMouseDown: header.getResizeHandler(),
                          className: `resizer ltr ${header.column.getIsResizing() ? 'isResizing' : ''}`
                        }}>
                        <div className={classes.colorResizer} />
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => handleClick(row.id)}
              className={activeRow === row.id ? classes.activeRow : ''}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default VisitorsReportTable;
